import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../services/api";
import { DEFAULT_OWNER_PORTFOLIO_QUERY } from "../constants/ownerPortfolio";
import { fetchOwnerPortfolio } from "../services/ownerPortfolioService";
import { OwnerPatrimonyRequestStatus } from "../types/ownerDashboard.types";
import {
  OwnerPortfolioData,
  OwnerPortfolioQuery,
  PortfolioMetricsDays,
  PortfolioSituation,
  PortfolioSort,
} from "../types/ownerPortfolio.types";

interface UseOwnerPortfolioOptions {
  accessToken: string | null;
  enabled: boolean;
}

interface UseOwnerPortfolioResult {
  data: OwnerPortfolioData | null;
  status: OwnerPatrimonyRequestStatus;
  query: OwnerPortfolioQuery;
  setSituation: (situation: PortfolioSituation | null) => void;
  setCondominium: (condominium: string | null) => void;
  setSort: (sort: PortfolioSort) => void;
  setMetricsDays: (metricsDays: PortfolioMetricsDays) => void;
  clearFilters: () => void;
  retry: () => Promise<void>;
}

export function useOwnerPortfolio({
  accessToken,
  enabled,
}: UseOwnerPortfolioOptions): UseOwnerPortfolioResult {
  const [data, setData] = useState<OwnerPortfolioData | null>(null);
  const [status, setStatus] = useState<OwnerPatrimonyRequestStatus>("idle");
  const [query, setQuery] = useState<OwnerPortfolioQuery>(DEFAULT_OWNER_PORTFOLIO_QUERY);
  const latestRequestIdRef = useRef(0);
  const queryRef = useRef(query);

  const loadPortfolio = useCallback(
    async (nextQuery: OwnerPortfolioQuery) => {
      if (!enabled || !accessToken) {
        return;
      }

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      queryRef.current = nextQuery;
      setQuery(nextQuery);
      setStatus("loading");

      try {
        api.defaults.headers.authorization = `Bearer ${accessToken}`;
        const response = await fetchOwnerPortfolio(nextQuery);

        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        setData(response);
        setStatus("success");
      } catch {
        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        setStatus("error");
      }
    },
    [accessToken, enabled]
  );

  useEffect(() => {
    if (!enabled || !accessToken) {
      setStatus("idle");
      return;
    }

    void loadPortfolio(queryRef.current);
  }, [accessToken, enabled, loadPortfolio]);

  const updateQuery = useCallback(
    (partial: Partial<OwnerPortfolioQuery>) => {
      void loadPortfolio({ ...queryRef.current, ...partial });
    },
    [loadPortfolio]
  );

  return {
    data,
    status,
    query,
    setSituation: (situation) => updateQuery({ situation }),
    setCondominium: (condominium) => updateQuery({ condominium }),
    setSort: (sort) => updateQuery({ sort }),
    setMetricsDays: (metricsDays) => updateQuery({ metricsDays }),
    clearFilters: () =>
      updateQuery({
        situation: null,
        condominium: null,
      }),
    retry: () => loadPortfolio(queryRef.current),
  };
}
